import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useRecoilState } from 'recoil';
import { Box, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Typography } from '@mui/material';

import useAlert from '../../../../hooks/useAlert';
import useMobileDetect from '../../../../hooks/useMobileDetect';
import usePersonalInformationStepForm from '../../../../hooks/usePersonalInformationStepForm';
import backend from '../../../../lib/backend';
import { createSubmitHandler } from '../../../../lib/submitHandler';
import { commonApplicationProfileAtom } from '../../../../state/atoms/commonApplicationProfile';
import usStatesAutocomplete from '../../../../state/autocompletes/usStatesAutocomplete';
import { UsState } from '../../../../types/eligibility';
import { ValidationError } from '../../../../types/errors/validation';
import { ProfileCompletionStepComponentBaseProps } from '../../../../types/ProfileCompletionStep';
import AppTextField from '../../../input/AppTextField';
import Autocomplete from '../../../input/Autocomplete';
import { CitizenshipStatusInput } from '../../../input/selectInputs';
import ProfilePopUpButtons from '../ProfilePopUpButtons';

type PersonalInformationStepProps = ProfileCompletionStepComponentBaseProps;

const PersonalInformationStep: FunctionComponent<
  PersonalInformationStepProps
> = ({ title, description, closeModal, navigateNext }) => {
  const [ fieldErrorHighlightEnabled, setFieldErrorHighlightEnabled ]
    = useState(false);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ isContinueLoading, setIsContinueLoading ] = useState(false);
  const [ isSaveAndExitLoading, setIsSaveAndExitLoading ] = useState(false);
  const isMobile = useMobileDetect();
  const [ profile, setProfile ] = useRecoilState(commonApplicationProfileAtom);

  const { submitForm, setFieldsFromProfile, fields, validStates }
    = usePersonalInformationStepForm();
  const { showSuccessAlert, showErrorAlert } = useAlert();

  useEffect(() => {
    if (profile) {
      setFieldsFromProfile(profile);

      return;
    }

    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        const { data } = await backend.getCommonApplicationProfile();
        setProfile(data);
      } catch (err) {
        console.error(err);
        showErrorAlert('Could not load your personal information');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [ profile, setProfile, setFieldsFromProfile, showErrorAlert ]);

  const submit = useCallback(async () => {
    setFieldErrorHighlightEnabled(true);

    try {
      const updatedProfile = await submitForm();
      setProfile(updatedProfile);
      showSuccessAlert('Personal information updated!');

      return true;
    } catch (err) {
      if (err instanceof ValidationError) {
        showErrorAlert('Please review the form and fix the errors');
      } else {
        showErrorAlert('Unknown error occurred during update');
      }

      return false;
    }
  }, [ setProfile, submitForm, showSuccessAlert, showErrorAlert ]);

  const spacingConfig = {
    lg: 3,
    xs: 1,
  };

  return (
    <Box id='personal-info-form'>
      <DialogTitle id='personal-info-form-title'>
        <Typography align='center' variant={isMobile ? 'h3' : 'h2'}>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0, mb: 3 }}>
        <DialogContentText id='personal-info-form-description'>
          <Typography align='center' variant={isMobile ? 'subtitle2' : 'subtitle1'}>
            {description}
          </Typography>
        </DialogContentText>
        <Grid container item mt={3} rowGap={1} width='100%'>
          <Grid container direction='row' spacing={spacingConfig}>
            <Grid item lg={6} xs={12}>
              <AppTextField
                error={
                  fieldErrorHighlightEnabled
                  && !validStates.applicant_first_name
                }
                field={fields.applicant_first_name}
                id='student-first-name'
                label='First Name'
              />
            </Grid>
            <Grid item lg={6} xs={12}>
              <AppTextField
                error={
                  fieldErrorHighlightEnabled
                  && !validStates.applicant_last_name
                }
                field={fields.applicant_last_name}
                id='student-last-name'
                label='Last Name'
              />
            </Grid>
          </Grid>
          <Grid container direction='row' spacing={spacingConfig}>
            <Grid item lg={6} xs={12}>
              <AppTextField
                disabled
                error={fieldErrorHighlightEnabled && !validStates.applicant_email}
                field={fields.applicant_email}
                id='student-email'
                label='Email'
              />
            </Grid>
            <Grid item lg={6} xs={12}>
              <AppTextField
                error={
                  fieldErrorHighlightEnabled
                  && !validStates.applicant_phone_number
                }
                field={fields.applicant_phone_number}
                id='student-phone'
                label='Phone Number'
              />
            </Grid>
          </Grid>
          <Grid container direction='row' spacing={spacingConfig}>
            <Grid item lg={6} xs={12}>
              <CitizenshipStatusInput field={fields.citizenship_status} />
            </Grid>
          </Grid>
        </Grid>
        <Grid container item mt={{ xs: 1, lg: 3 }} rowGap={1} width='100%'>
          <Grid container direction='row' spacing={spacingConfig}>
            <Grid item lg={6} xs={12}>
              <AppTextField
                error={fieldErrorHighlightEnabled && !validStates.address_line}
                field={fields.address_line}
                id='address-line'
                label='Address'
              />
            </Grid>
            <Grid item lg={6} xs={12}>
              <AppTextField
                error={fieldErrorHighlightEnabled && !validStates.address_detail}
                field={fields.address_detail}
                id='apt-suite'
                label='Apt / Suite No. (Optional)'
              />
            </Grid>
          </Grid>
          <Grid container direction='row' spacing={spacingConfig}>
            <Grid item lg={6} xs={12}>
              <AppTextField
                error={fieldErrorHighlightEnabled && !validStates.city}
                field={fields.city}
                id='city'
                label='City'
              />
            </Grid>
            <Grid item lg={6} xs={12}>
              <Autocomplete
                autocompleteState={usStatesAutocomplete}
                data-cy='state-input'
                error={fieldErrorHighlightEnabled && !validStates.state}
                field={fields.state}
                getOptionLabel={(option: UsState) => option.description}
                helperText={fieldErrorHighlightEnabled && !validStates.state ? 'Please select a state' : ''}
                id='state-input'
                isOptionEqualToValue={
                  (option: UsState, value: UsState) =>
                    option.abbreviation === value.abbreviation
                }
                label='State'
                noOptionsText='Start typing to filter states'
                sx={{ m: 0 }}
                variant='outlined'
              />
            </Grid>
          </Grid>
          <Grid container direction='row' spacing={spacingConfig}>
            <Grid item lg={6} xs={12}>
              <AppTextField
                error={fieldErrorHighlightEnabled && !validStates.zip}
                field={fields.zip}
                id='zip'
                label='Zip'
              />
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 0 }}>
        <ProfilePopUpButtons
          disabled={isLoading || isContinueLoading || isSaveAndExitLoading}
          loadingContinue={isContinueLoading}
          loadingSaveAndExit={isSaveAndExitLoading}
          onContinue={
            createSubmitHandler({
              startLoadingState: () => setIsContinueLoading(true),
              handleSubmit: submit,
              onSuccess: navigateNext,
              onFinally: () => setIsContinueLoading(false),
            })
          }
          onSaveAndExit={
            createSubmitHandler({
              startLoadingState: () => setIsSaveAndExitLoading(true),
              handleSubmit: submit,
              onSuccess: closeModal,
              onFinally: () => setIsSaveAndExitLoading(false),
            })
          }
          showContinue={true}
          showSaveAndExit={isMobile}
        />
      </DialogActions>
    </Box>
  );
};

export default PersonalInformationStep;
